import { Inject, Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';

import { LatLng } from '@ridango/core';

import * as OpenTripPlannerSelectors from './open-trip-planner.selectors';
import * as actions from './open-trip-planner.actions';
import { getNearbyStops } from './open-trip-planner.actions';
import {
    Departure,
    ExploreMapRoute,
    Itinerary,
    JourneyDirection,
    JourneyMapPlan,
    PlannerStop,
    Route,
    SearchRecentlyJourney,
    Stop,
    StopMarker
} from '../open-trip-planner.model';
import { JourneyPlannerFilter } from '../journey-planner-filter.model';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, scan, take } from 'rxjs/operators';
import { OpenTripPlannerService } from '../open-trip-planner.service';
import Env from '@ridango/core/env';
import { LatLng as LatLngLeaflet } from 'leaflet';

@Injectable()
export class OpenTripPlannerFacade {
    routes$ = this.store.pipe(
        select(OpenTripPlannerSelectors.routes),
        map((routes) => (this.env.EXPLORE?.ENABLE_ROUTES_SORTING ? this.sortRoutes(routes) : routes))
    );
    routesGroups$ = this.store.pipe(
        select(OpenTripPlannerSelectors.routesGroups),
        map((groups) =>
            this.env.EXPLORE?.ENABLE_ROUTES_SORTING
                ? groups.map((group) => ({
                      ...group,
                      routes: this.sortRoutes(group.routes)
                  }))
                : groups
        )
    );
    stops$ = this.store.pipe(select(OpenTripPlannerSelectors.stops));
    allFormattedStops$ = this.store.pipe(select(OpenTripPlannerSelectors.allFormattedStops));
    location$ = this.store.pipe(select(OpenTripPlannerSelectors.location));
    search$ = this.store.pipe(select(OpenTripPlannerSelectors.search));
    searchRecentlyJourneys$ = this.store.pipe(select(OpenTripPlannerSelectors.searchRecentlyJourneys));
    plan$ = this.store.pipe(select(OpenTripPlannerSelectors.plan));
    itineraries$ = this.store.pipe(select(OpenTripPlannerSelectors.itineraries));
    selectedItineraryForDetails$ = this.store.pipe(select(OpenTripPlannerSelectors.selectedItineraryForDetails));
    trips$ = this.store.pipe(select(OpenTripPlannerSelectors.trips));
    alerts$ = this.store.pipe(select(OpenTripPlannerSelectors.alerts));

    originSelected = false;

    exploreMapStops$ = this.store.pipe(select(OpenTripPlannerSelectors.exploreMapStops));
    exploreMapRoute$ = this.store.pipe(select(OpenTripPlannerSelectors.exploreMapRoute));
    journeyMapPlan$ = this.store.pipe(select(OpenTripPlannerSelectors.journeyMapPlan));
    nearbyStops$ = this.store.pipe(select(OpenTripPlannerSelectors.nearbyStops));
    nearbyStopsLoading$ = this.store.pipe(select(OpenTripPlannerSelectors.nearbyStopsLoading));

    nearbyStopsWithAlerts$ = combineLatest([this.nearbyStops$, this.alerts$]).pipe(
        map(([stops, alerts]) =>
            stops.map((stop) => ({
                ...stop,
                disruptionMessages: alerts.filter((alert) => alert.informedEntities?.some((e) => e.stopId === stop.id))
            }))
        )
    );

    private selectedStopWithDeparturesSubject$ = new Subject<{ stop: Stop; departures: Departure[] }>();
    private routePatternStopSubject$ = new Subject<Stop>();
    private triggerLocationInit = new Subject();
    selectedStopWithDepartures$ = this.selectedStopWithDeparturesSubject$.asObservable();
    сurrentCoordinates: LatLngLeaflet;
    routePatternStop$ = this.routePatternStopSubject$.asObservable();
    triggerLocationInit$ = this.triggerLocationInit.asObservable();
    private stopLocate = new Subject();
    stopLocate$ = this.stopLocate.asObservable();
    private readonly refreshStopTimes = new Subject<PlannerStop['stopId']>();
    refreshStopTimes$ = this.refreshStopTimes.pipe(
        scan((acc, value) => (acc.includes(value) || !value ? acc : [...acc, value]), []),
        debounceTime(1000)
    );

    constructor(
        private store: Store,
        private otpService: OpenTripPlannerService,
        @Inject('ENV') public env: Env
    ) {}

    getAllRoutes(): void {
        this.store.dispatch(actions.getAllRoutes());
    }

    getAllAlerts(): void {
        this.store.dispatch(actions.getAllAlerts());
    }

    getAllStops(): void {
        this.store.dispatch(actions.getAllStops());
    }

    getTripsByRouteId(routeId: string): void {
        this.store.dispatch(actions.loadTripsByRouteId({ routeId }));
    }

    getRoutesGroups(): void {
        this.store.dispatch(actions.getAllRoutesGroups());
    }

    setCurrentLocation(coordinates: LatLng, hasError?: boolean): void {
        this.store.dispatch(actions.setCurrentLocation({ coordinates: coordinates, hasError: !!hasError }));

        if (coordinates && !this.originSelected) {
            this.store.dispatch(
                actions.setSearchOrigin({ name: 'CURRENT_LOCATION', coordinates: coordinates, isCurrentLocation: true })
            );
            this.originSelected = true;
            return;
        }
        !coordinates && !this.originSelected && this.clearSearch();
    }

    refreshStopTime(stopId: string) {
        this.refreshStopTimes.next(stopId);
    }

    setSearchAddress(
        type: JourneyDirection,
        name?: string,
        coordinates?: LatLng,
        isCurrentLocation?: boolean,
        isLocationFromMap?: boolean,
        municipalityName?: string,
        fromStopId?: number,
        toStopId?: number
    ): void {
        if (type === JourneyDirection.Origin) {
            this.originSelected = true;
            this.store.dispatch(
                actions.setSearchOrigin({
                    name,
                    coordinates,
                    isCurrentLocation: !!isCurrentLocation,
                    isLocationFromMap: !!isLocationFromMap,
                    municipalityName,
                    fromStopId: fromStopId
                })
            );
        } else {
            this.store.dispatch(
                actions.setSearchDestination({
                    name,
                    coordinates,
                    isCurrentLocation: !!isCurrentLocation,
                    isLocationFromMap: !!isLocationFromMap,
                    municipalityName,
                    toStopId: toStopId
                })
            );
        }
    }

    clearSearch(): void {
        this.store.dispatch(actions.clearSearch());
    }

    setSearchDisabled(disabled: boolean): void {
        this.store.dispatch(actions.setSearchDisabled({ disabled }));
    }

    swapSearchOriginAndDestination() {
        this.store.dispatch(actions.swapSearchOriginAndDestination());
    }

    setSearchDateAndTime(date: string, time: string, arrive: boolean = false): void {
        this.store.dispatch(actions.setSearchDateAndTime({ date, time, arrive }));
    }

    getPlan(payload): void {
        this.store.dispatch(actions.getPlan(payload));
    }

    setCurrentCoordinates(value) {
        this.сurrentCoordinates = value;
    }

    setJourneyPlannerFilters(filters: JourneyPlannerFilter): void {
        this.store.dispatch(actions.setJourneyPlannerFilters({ filters }));
    }

    setExploreMapStops(stops: Stop[]) {
        const stopMarkers: StopMarker[] = stops.map((stop) => ({
            name: stop.name,
            code: stop.code,
            dist: stop.dist,
            latlng: { lat: stop.lat, lng: stop.lon } as LatLng
        }));
        this.store.dispatch(actions.setExploreMapStops({ exploreMapStops: stopMarkers }));
    }

    setExploreMapRoute(exploreMapRoute: ExploreMapRoute) {
        this.store.dispatch(actions.setExploreMapRoute({ exploreMapRoute: exploreMapRoute }));
    }

    clearExploreMapStops() {
        this.store.dispatch(actions.setExploreMapStops({ exploreMapStops: [] }));
    }

    clearExploreMapRoute() {
        this.store.dispatch(actions.setExploreMapRoute(null));
    }

    setJourneyMapPlan(plan: JourneyMapPlan) {
        this.store.dispatch(actions.setJourneyMapPlan({ journeyMapPlan: plan }));
    }

    clearJourneyMapPlan() {
        this.store.dispatch(actions.setJourneyMapPlan({ journeyMapPlan: null }));
    }

    setSelectedItineraryForDetails(itinerary: Itinerary) {
        this.store.dispatch(actions.setSelectedItineraryForDetails({ itinerary: itinerary }));
    }

    clearSearchRecentlyJourneys() {
        this.store.dispatch(actions.clearSearchRecentlyJourneysFromStorage());
    }

    clearPlanResults(): void {
        this.store.dispatch(actions.clearPlanResults());
    }

    addSearchRecentlyJourneys(payload: SearchRecentlyJourney) {
        this.store.dispatch(actions.addSearchRecentlyJourneyToStorage(payload));
    }

    getAllSearchRecentlyJourneys(): void {
        this.store.dispatch(actions.getAllSearchRecentlyJourneys());
    }

    triggerLocationLocate() {
        this.triggerLocationInit.next();
    }

    setRoutePatternStop(stop: Stop) {
        this.routePatternStopSubject$.next(stop);
    }

    setSelectedStop(stop: Stop) {
        if (!stop) {
            this.selectedStopWithDeparturesSubject$.next({ stop, departures: [] });

            return;
        }

        const departuresSearchRangeSeconds = 60 * 60 * 24;

        return this.otpService
            .getUpcomingDepartures({
                stopId: stop.id,
                timeRangeSeconds: departuresSearchRangeSeconds,
                fromTime: this.otpService.getCurrentSeconds()
            })
            .pipe(
                catchError(() => of([])),
                take(1)
            )
            .subscribe((departures) => {
                this.selectedStopWithDeparturesSubject$.next({ stop, departures });
            });
    }

    fetchNearbyStops(latitude: number, longitude: number, nearbyRadiusMeters: number) {
        this.store.dispatch(
            getNearbyStops({
                latitude: latitude,
                longitude: longitude,
                nearbyRadiusMeters: nearbyRadiusMeters
            })
        );
    }

    getStopByNameFromMemory(stopName: string, coordinates?: LatLng, stopId?: number): Stop {
        return this.otpService.getStopByNameFromMemory(stopName, coordinates, stopId);
    }

    triggerLocateStop() {
        this.stopLocate.next();
    }

    sortRoutes(routes: Route[]): Route[] {
        if (!routes) return [];
        if (!this.env.EXPLORE?.ENABLE_ROUTES_SORTING) {
            return routes;
        }

        const decoratedRoutes = routes.map((route) => {
            const shortName = route.shortName || '';
            const startsWithLetter = /^[A-Za-z]/.test(shortName);

            let letterPart = '';
            let numValue = 0;
            let remainder = '';

            if (startsWithLetter) {
                const letterMatch = shortName.match(/^[A-Za-z]+/);
                letterPart = letterMatch ? letterMatch[0].toUpperCase() : '';

                const numMatch = shortName.match(/\d+/);
                numValue = numMatch ? parseInt(numMatch[0], 10) : 0;

                remainder = shortName.substring(letterPart.length);
                if (numMatch) {
                    const numStr = numMatch[0];
                    const numIndex = remainder.indexOf(numStr);
                    if (numIndex !== -1) {
                        remainder = remainder.substring(numIndex + numStr.length);
                    }
                }
            } else {
                const numMatch = shortName.match(/^\d+/);
                if (numMatch) {
                    numValue = parseInt(numMatch[0], 10);
                    remainder = shortName.substring(numMatch[0].length);
                } else {
                    remainder = shortName;
                }
            }

            return {
                route,
                startsWithLetter,
                letterPart,
                numValue,
                remainder
            };
        });

        decoratedRoutes.sort((a, b) => {
            if (a.startsWithLetter !== b.startsWithLetter) {
                return a.startsWithLetter ? 1 : -1;
            }

            if (a.startsWithLetter) {
                if (a.letterPart !== b.letterPart) {
                    return a.letterPart.localeCompare(b.letterPart);
                }

                if (a.numValue !== b.numValue) {
                    return a.numValue - b.numValue;
                }

                return a.remainder.localeCompare(b.remainder);
            } else {
                if (a.numValue !== b.numValue) {
                    return a.numValue - b.numValue;
                }

                return a.remainder.localeCompare(b.remainder);
            }
        });

        return decoratedRoutes.map((item) => item.route);
    }
}
